// @wc-ignore-file

import type { ChangeRequest, CrPriority } from "@shared/types/supabase.types";

/**
 * CR board sort (CR-118). Pure and React-free: the page sorts the filtered
 * list once (memoised) and hands it to the board, whose `groupByStatus` keeps
 * input order within each column — so sorting never moves a card between
 * columns.
 */

export type CrSortField = "updated" | "sequential_id" | "priority";
export type CrSortDirection = "asc" | "desc";

export interface CrSortOption {
  field: CrSortField;
  direction: CrSortDirection;
}

/** Order the sort control offers the fields and directions in. */
export const CR_SORT_FIELDS: readonly CrSortField[] = ["updated", "sequential_id", "priority"];
export const CR_SORT_DIRECTIONS: readonly CrSortDirection[] = ["desc", "asc"];

export const DEFAULT_CR_SORT: CrSortOption = {
  field: "updated",
  direction: "desc",
};

/**
 * Urgency rank — higher is more urgent. NOT `CR_PRIORITIES`, which is the
 * Linear-style picker order ("none" first) and would rank "none" above
 * "urgent".
 */
export const CR_PRIORITY_RANK: Record<CrPriority, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
};

function toTime(value: string | null | undefined): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const time = Date.parse(value);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

/**
 * "Last updated" timestamp: `updated_at ?? created_at`.
 * `change_requests.updated_at` stays NULL until a CR's first edit, so a
 * never-edited CR falls back to `created_at`. An invalid timestamp ranks as
 * the oldest rather than throwing.
 */
export function crLastUpdatedAt(cr: Pick<ChangeRequest, "updated_at" | "created_at">): number {
  return toTime(cr.updated_at ?? cr.created_at);
}

function priorityRank(priority: CrPriority): number {
  // The column is text + CHECK, so a value outside the union (a row written by
  // a newer server) is possible: it ranks below "none". `hasOwn` keeps
  // prototype keys such as "constructor" out of the lookup.
  return Object.hasOwn(CR_PRIORITY_RANK, priority) ? CR_PRIORITY_RANK[priority] : -1;
}

function compareNumbers(a: number, b: number): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function compareField(a: ChangeRequest, b: ChangeRequest, field: CrSortField): number {
  switch (field) {
    case "updated":
      return compareNumbers(crLastUpdatedAt(a), crLastUpdatedAt(b));
    case "sequential_id":
      return compareNumbers(a.sequential_id, b.sequential_id);
    case "priority":
      return compareNumbers(priorityRank(a.priority), priorityRank(b.priority));
    default: {
      const unknownField: never = field;
      void unknownField;
      return 0;
    }
  }
}

/**
 * Chosen field in the chosen direction; ties fall through a fixed chain —
 * last updated (newest first), then sequential_id (highest first), then id
 * (ascending) — that does not flip with the direction, so equal cards keep a
 * stable, deterministic order.
 */
export function compareChangeRequests(
  a: ChangeRequest,
  b: ChangeRequest,
  sort: CrSortOption,
): number {
  const primary = compareField(a, b, sort.field);
  if (primary !== 0) return sort.direction === "asc" ? primary : -primary;

  const updated = compareNumbers(crLastUpdatedAt(b), crLastUpdatedAt(a));
  if (updated !== 0) return updated;

  const sequential = compareNumbers(b.sequential_id, a.sequential_id);
  if (sequential !== 0) return sequential;

  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/** Returns a NEW sorted array; the input is never mutated. Memoise the call —
 * a fresh array on every render re-runs the board's grouping effect. */
export function sortChangeRequests<T extends ChangeRequest>(
  crs: readonly T[],
  sort: CrSortOption,
): T[] {
  return [...crs].sort((a, b) => compareChangeRequests(a, b, sort));
}

// URL search param (CR-140)

export function isCrSortField(value: string): value is CrSortField {
  return (CR_SORT_FIELDS as readonly string[]).includes(value);
}

export function isCrSortDirection(value: string): value is CrSortDirection {
  return (CR_SORT_DIRECTIONS as readonly string[]).includes(value);
}

/**
 * CR-140: the sort rides the board URL as `?sort=<field>-<direction>` (e.g.
 * `priority-asc`), like the priority and type filters, so it survives reloads
 * and Back and can be shared. The default sort is never written — a board URL
 * without `sort` means "last updated, newest first".
 */
export type CrSortSearchValue = `${CrSortField}-${CrSortDirection}`;

function isDefaultCrSort(sort: CrSortOption): boolean {
  return sort.field === DEFAULT_CR_SORT.field && sort.direction === DEFAULT_CR_SORT.direction;
}

/** `<field>-<direction>` → option, or `undefined` for anything else.
 * Directions never contain a dash, so the last dash splits. */
function splitCrSortValue(value: string): CrSortOption | undefined {
  const dash = value.lastIndexOf("-");
  if (dash <= 0) return undefined;
  const field = value.slice(0, dash);
  const direction = value.slice(dash + 1);
  if (!isCrSortField(field) || !isCrSortDirection(direction)) return undefined;
  return { field, direction };
}

/** Validates a raw `sort` search param. Anything that is not a known
 * non-default `<field>-<direction>` string reads as `undefined`. */
export function parseCrSortSearch(value: unknown): CrSortSearchValue | undefined {
  if (typeof value !== "string") return undefined;
  const sort = splitCrSortValue(value);
  if (!sort || isDefaultCrSort(sort)) return undefined;
  return `${sort.field}-${sort.direction}`;
}

/** The sort a validated `sort` param stands for (`undefined` → default). */
export function crSortFromSearch(value: CrSortSearchValue | undefined): CrSortOption {
  return (value && splitCrSortValue(value)) || DEFAULT_CR_SORT;
}

/** The `sort` param for a sort; the default collapses to `undefined`. */
export function crSortToSearch(sort: CrSortOption): CrSortSearchValue | undefined {
  return isDefaultCrSort(sort) ? undefined : `${sort.field}-${sort.direction}`;
}
